import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import CheckIcon from "@mui/icons-material/Check";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Box, Typography, useTheme } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import {
  BasicSwitch,
  ShowToastError,
  ShowToastSuccess,
  getStructureDataErrorKey,
  normalizeStructureData,
  validateOverrideStructureData,
  type StructureDataEnvelope,
} from "@platformx/utilities";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import JSONInput from "react-json-editor-ajrm";
import locale from "react-json-editor-ajrm/locale/en";

const StructureDataPopup = ({ closeStructureData, seoInfo, keyname, handleChange, isOpen }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  // Envelope { use, auto, manual } — legacy plain JSON-LD normalizes into auto.
  const [envelope, setEnvelope] = useState<StructureDataEnvelope>(
    normalizeStructureData(seoInfo?.[keyname]),
  );
  const overrideMarkup = envelope.use === "manual";
  // Seed from stored manual (fallback auto) so a page opened already in manual mode shows its JSON.
  const [overrideText, setOverrideText] = useState<string>(() =>
    envelope.use === "manual"
      ? JSON.stringify(envelope.manual ?? envelope.auto ?? {}, undefined, 2)
      : "",
  );

  const handleOverrideMarkupChange = (event) => {
    const { checked } = event.target;
    setEnvelope((prev) => {
      const manual = checked && !prev.manual ? { ...prev.auto } : prev.manual;
      if (checked) {
        setOverrideText(JSON.stringify(manual ?? {}, undefined, 2));
      }
      return { ...prev, manual, use: checked ? "manual" : "auto" };
    });
  };

  const handleOverrideTextChange = (event) => {
    const text = event.target.value;
    setOverrideText(text);
    try {
      const parsed = JSON.parse(text);
      if (validateOverrideStructureData(parsed, false).isValid) {
        setEnvelope((prev) => ({ ...prev, manual: parsed }));
      }
    } catch {
      // Invalid while typing — kept local until it validates.
    }
  };

  const validateOverrideText = () => {
    if (overrideText.trim() === "") {
      return validateOverrideStructureData(undefined, false);
    }
    try {
      return validateOverrideStructureData(JSON.parse(overrideText), false);
    } catch (error: any) {
      return validateOverrideStructureData(undefined, true, error?.message);
    }
  };

  const handleCheckIconClick = () => {
    if (overrideMarkup) {
      const result = validateOverrideText();
      if (!result.isValid) {
        ShowToastError(t(getStructureDataErrorKey(result.code)));
        return;
      }
    }
    handleChange(envelope);
    ShowToastSuccess(`${t("page_structure_data")} ${t("saved")}`);
    closeStructureData();
  };
  const handleCopyStructureData = () => {
    navigator.clipboard.writeText(
      JSON.stringify(overrideMarkup ? (envelope.manual ?? envelope.auto) : envelope.auto),
    );
    ShowToastSuccess(`${t("page_structure_data")} ${t("copied")}`);
  };
  return (
    <Dialog
      fullWidth
      open={isOpen}
      onClose={() => closeStructureData(false)}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      sx={{
        ".Platform-x-Dialog-paper": {
          maxWidth: { xs: "100%", sm: "580px", lg: "590px" },
          width: { xs: "100%", sm: "580px", lg: "590px" },
          margin: { xs: "0px" },
          border: "1px solid #89909a",
          position: { xs: "absolute", md: "inherit" },
          bottom: { xs: 0 },
          borderBottomLeftRadius: { xs: 0, md: 5 },
          borderBottomRightRadius: { xs: 0, md: 5 },
        },
      }}>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "12px 15px",
          borderBottom: "1px solid #89909a",
        }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            onClick={() => closeStructureData(false)}
            data-testid='close-structure-data'
            sx={{
              display: {
                xs: "flex",
                sm: "none",
                marginRight: "10px",
              },
            }}>
            <ArrowBackIosIcon fontSize='small' />
          </Box>
          <Typography variant='h4bold'>{t("page_structure_data")}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            variant='p4regular'
            sx={{
              display: "flex",
              alignItems: "center",
              margin: "0 20px 0 0",
              gap: "8px",
              color: theme?.palette?.customColor?.PRIMARY || "#2d2d39",
            }}>
            {t("page_schema_markup_override")}
            <BasicSwitch
              color={theme?.palette?.customColor?.PRIMARY}
              bgcolor={theme?.palette?.customColor?.PLACEHOLDER}
              onChange={handleOverrideMarkupChange}
              checked={overrideMarkup}
              ariaLabel={t("page_schema_markup_override")}
            />
          </Typography>
          <Box
            onClick={handleCopyStructureData}
            data-testid='copy-structure-data'
            sx={{
              cursor: "pointer",
              fontSize: "14px",
              marginRight: "20px",
              display: "flex",
            }}>
            <ContentCopyIcon />
          </Box>
          <Box
            onClick={handleCheckIconClick}
            data-testid='check-icon-click'
            sx={{
              cursor: "pointer",
              fontSize: "14px",
              display: "flex",
            }}>
            <CheckIcon />
          </Box>
        </Box>
      </DialogActions>
      <DialogContent
        sx={{
          padding: "10px 20px",
        }}>
        {overrideMarkup ? (
          <Box
            component='textarea'
            data-testid='structure-data-override-textarea'
            value={overrideText}
            onChange={handleOverrideTextChange}
            spellCheck={false}
            sx={{
              width: "100%",
              boxSizing: "border-box",
              height: { xs: "45vh", sm: "50vh", md: "55vh" },
              minHeight: "240px",
              padding: "12px",
              borderRadius: "5px",
              border: `1px solid ${theme?.palette?.customColor?.LINE || "#D9DBE9"}`,
              outline: "none",
              backgroundColor: theme?.palette?.customColor?.INPUT || "#EFF0F6",
              color: theme?.palette?.customColor?.PRIMARY || "#14142B",
              fontFamily: "'Roboto Mono', monospace",
              fontSize: "16px",
              lineHeight: 1.5,
              resize: "vertical",
              "&:focus-visible": {
                outline: `2px solid ${theme?.palette?.customColor?.PRIMARY || "#2d2d39"}`,
                outlineOffset: "-2px",
              },
            }}
          />
        ) : (
          <JSONInput
            id='json-editor'
            confirmGood={false}
            placeholder={envelope.auto}
            viewOnly
            theme='light_mitsuketa_tribute'
            locale={locale}
            height='100%'
            width='100%'
            colors={{
              string: "#1984bc",
              keys: "#000000",
              colon: "#000000",
              default: "#000000",
            }}
            style={{
              body: { fontSize: "16px" },
              fontFamily: "Roboto Mono",
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default StructureDataPopup;
